import React from "react";

interface GrowingPlantProps {
  percent: number; // 0-100
}

export default function GrowingPlant({ percent }: GrowingPlantProps) {
  const p = Math.max(0, Math.min(100, percent));

  // Plant grows through stages based on percent
  // 0%: seed in soil
  // 1-15%: tiny sprout
  // 16-35%: small stem with 2 leaves
  // 36-60%: taller stem with 4 leaves
  // 61-85%: tall plant with leaves and a bud
  // 86-99%: flowering plant
  // 100%: full bloom with sparkles

  const soilY = 140;
  const stemHeight = (p / 100) * 90; // max 90px tall
  const stemTop = soilY - stemHeight;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="80" height="160" viewBox="0 0 80 160" className="overflow-visible">
        {/* Pot */}
        <path d="M20 140 L25 158 L55 158 L60 140 Z" fill="#8B5E3C" />
        <rect x="16" y="136" width="48" height="6" rx="2" fill="#A0714F" />
        {/* Soil */}
        <ellipse cx="40" cy="140" rx="20" ry="4" fill="#5C3D2E" />

        {p === 0 && (
          /* Seed */
          <ellipse cx="40" cy="135" rx="4" ry="3" fill="#8B7355" />
        )}

        {p > 0 && (
          <>
            {/* Stem */}
            <line
              x1="40"
              y1={soilY}
              x2="40"
              y2={stemTop}
              stroke="#4CAF50"
              strokeWidth={p > 50 ? 3 : 2}
              strokeLinecap="round"
            />

            {/* Leaves appear at different growth stages */}
            {p >= 10 && (
              /* First tiny leaf - right */
              <ellipse
                cx={46}
                cy={soilY - stemHeight * 0.4}
                rx={4 + (p > 30 ? 3 : 0)}
                ry={2 + (p > 30 ? 1 : 0)}
                fill="#66BB6A"
                transform={`rotate(-30 46 ${soilY - stemHeight * 0.4})`}
              />
            )}
            {p >= 20 && (
              /* Second leaf - left */
              <ellipse
                cx={34}
                cy={soilY - stemHeight * 0.5}
                rx={4 + (p > 40 ? 3 : 0)}
                ry={2 + (p > 40 ? 1 : 0)}
                fill="#66BB6A"
                transform={`rotate(30 34 ${soilY - stemHeight * 0.5})`}
              />
            )}
            {p >= 35 && (
              /* Third leaf - right higher */
              <ellipse
                cx={47}
                cy={soilY - stemHeight * 0.65}
                rx={5 + (p > 60 ? 2 : 0)}
                ry={2.5}
                fill="#81C784"
                transform={`rotate(-25 47 ${soilY - stemHeight * 0.65})`}
              />
            )}
            {p >= 45 && (
              /* Fourth leaf - left higher */
              <ellipse
                cx={33}
                cy={soilY - stemHeight * 0.75}
                rx={5 + (p > 60 ? 2 : 0)}
                ry={2.5}
                fill="#81C784"
                transform={`rotate(25 33 ${soilY - stemHeight * 0.75})`}
              />
            )}

            {/* Bud / Flower at the top */}
            {p >= 60 && p < 85 && (
              /* Bud */
              <ellipse
                cx={40}
                cy={stemTop}
                rx={4}
                ry={5}
                fill="#A5D6A7"
              />
            )}
            {p >= 85 && (
              /* Flower */
              <>
                {[0, 60, 120, 180, 240, 300].map((angle) => (
                  <ellipse
                    key={angle}
                    cx={40 + Math.cos((angle * Math.PI) / 180) * 7}
                    cy={stemTop + Math.sin((angle * Math.PI) / 180) * 7}
                    rx={p >= 95 ? 5 : 4}
                    ry={p >= 95 ? 7 : 5}
                    fill={p >= 100 ? "#FF8A65" : "#FFAB91"}
                    transform={`rotate(${angle} ${40 + Math.cos((angle * Math.PI) / 180) * 7} ${stemTop + Math.sin((angle * Math.PI) / 180) * 7})`}
                  />
                ))}
                <circle cx={40} cy={stemTop} r={4} fill="#FFD54F" />
              </>
            )}

            {/* Sparkles at 100% */}
            {p >= 100 && (
              <>
                <text x="15" y={stemTop - 10} fontSize="10" className="animate-pulse">✨</text>
                <text x="55" y={stemTop - 5} fontSize="10" className="animate-pulse">✨</text>
                <text x="35" y={stemTop - 20} fontSize="12" className="animate-pulse">🌟</text>
              </>
            )}
          </>
        )}
      </svg>

      <div className="text-xs text-muted-foreground font-mono">{Math.round(p)}%</div>
    </div>
  );
}
